import { CHANNELS } from "../../shared/channels";
import { MAX_TIERS, MAX_TIER_ITEMS, TIER_COLORS, TierImageDraft, TierList, TierListSummary, capInk, newTierId } from "../../shared/tierlists";
import { call, isWeb, listen, mediaSrc } from "../bridge";
import { byId, flashToast } from "../dom";
import { iconSvg } from "../icons";
import { t } from "../i18n";
import { onViewHidden, onViewShown } from "../navigation";
import { buzz } from "../tweens";

// tier lists: rank pictures into rows, S at the top. a picture moves three ways so it works with any hand: drag it,
// tap it and then tap a tier, or focus it and use the arrow keys. the list saves itself, and on the website it can be
// turned into a link anyone can open

const PICTURE_SIDE = 512;
const SEND_BATCH = 6;

let shelf: TierListSummary[] = [];
let list: TierList | null = null;
let selected = "";
let saveTimer = 0;
let drag: { id: string; pointer: number; x: number; y: number; started: boolean; ghost: HTMLElement | null; from: HTMLElement } | null = null;
let tierEditing = "";
let busy = 0;

const rack = () => byId("tl-rack");
const pool = () => byId("tl-pool");

function say(text: string): void {
  byId("tl-live").textContent = text;
}

// --- the shelf

function ago(iso: string): string {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return t("just now");
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function shelfCard(item: TierListSummary): HTMLElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = "tile tl-card";
  const media = document.createElement("span");
  media.className = "tile-media tl-covers";
  item.covers.forEach((url) => {
    const image = document.createElement("img");
    image.alt = "";
    image.loading = "lazy";
    image.decoding = "async";
    image.src = mediaSrc(url);
    media.append(image);
  });
  if (!item.covers.length) media.textContent = item.title.slice(0, 1).toUpperCase();
  const title = document.createElement("span");
  title.className = "tile-title";
  title.textContent = item.title;
  const meta = document.createElement("span");
  meta.className = "tile-meta";
  meta.textContent = `${item.ranked}/${item.count} ${t("ranked")} · ${ago(item.updatedAt)}`;
  card.append(media, title, meta);
  if (item.shared) {
    const badge = document.createElement("span");
    badge.className = "tile-badge";
    badge.textContent = t("link on");
    card.append(badge);
  }
  card.addEventListener("click", () => void openList(item.id));
  return card;
}

function renderShelf(): void {
  byId("tl-shelf").replaceChildren(...shelf.map(shelfCard));
  byId("tl-empty").hidden = shelf.length > 0;
  byId("tl-count").textContent = shelf.length ? String(shelf.length) : "";
}

async function reloadShelf(): Promise<void> {
  try {
    shelf = await call<TierListSummary[]>(CHANNELS.tierlistsList);
    renderShelf();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not load the tier lists"));
  }
}

async function create(): Promise<void> {
  try {
    const made = await call<TierList>(CHANNELS.tierlistsCreate, "");
    await reloadShelf();
    show(made);
    byId<HTMLInputElement>("tl-title").select();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not make a tier list"));
  }
}

// --- the editor

function show(next: TierList): void {
  list = next;
  selected = "";
  byId("tl-shelf-block").hidden = true;
  byId("tl-editor").hidden = false;
  byId<HTMLInputElement>("tl-title").value = next.title;
  byId("tl-share").hidden = !isWeb();
  paintLink();
  render();
}

async function openList(id: string): Promise<void> {
  try {
    show(await call<TierList>(CHANNELS.tierlistsGet, id));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not open that tier list"));
  }
}

async function closeList(): Promise<void> {
  await flushSave();
  list = null;
  selected = "";
  byId("tl-editor").hidden = true;
  byId("tl-shelf-block").hidden = false;
  await reloadShelf();
}

function scheduleSave(): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => void flushSave(), 500);
}

async function flushSave(): Promise<void> {
  window.clearTimeout(saveTimer);
  saveTimer = 0;
  if (!list) return;
  const sending = list;
  try {
    await call<TierList>(CHANNELS.tierlistsSave, sending);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save that"));
  }
}

function labelOf(id: string): string {
  const item = list?.items[id];
  if (!item) return "";
  return item.label || t("picture");
}

// where a picture is now, for the screen reader and the hint
function whereIs(id: string): string {
  if (!list) return "";
  const at = list.tiers.findIndex((tier) => tier.items.includes(id));
  return at >= 0 ? t("in tier {name}", { name: list.tiers[at].label || String(at + 1) }) : t("in the unranked tray");
}

function tileEl(id: string): HTMLElement {
  const item = list!.items[id];
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = "tl-tile";
  tile.dataset.id = id;
  tile.classList.toggle("sel", selected === id);
  tile.setAttribute("aria-pressed", String(selected === id));
  tile.setAttribute("aria-label", `${labelOf(id)}, ${whereIs(id)}`);
  const image = document.createElement("img");
  image.alt = "";
  image.draggable = false;
  image.loading = "lazy";
  image.decoding = "async";
  image.src = mediaSrc(item.url || "");
  tile.append(image);
  if (item.label) {
    const name = document.createElement("span");
    name.textContent = item.label;
    tile.append(name);
  }
  tile.addEventListener("click", () => {
    if (tile.dataset.dragged) {
      delete tile.dataset.dragged;
      return;
    }
    select(selected === id ? "" : id);
  });
  tile.addEventListener("pointerdown", (event) => beginDrag(event, id, tile));
  tile.addEventListener("keydown", (event) => keyMove(event, id));
  return tile;
}

function render(focus = ""): void {
  if (!list) return;
  const current = list;
  rack().replaceChildren(...current.tiers.map((tier, at) => {
    const row = document.createElement("div");
    row.className = "tl-tier";
    row.dataset.tier = tier.id;
    const cap = document.createElement("button");
    cap.type = "button";
    cap.className = "tl-cap";
    cap.style.setProperty("--c", tier.color);
    cap.style.setProperty("--ink", capInk(tier.color));
    cap.textContent = tier.label || "·";
    cap.setAttribute("aria-label", selected ? t("place the picked picture in tier {name}", { name: tier.label || String(at + 1) }) : t("tier {name}, {count} pictures, press to change it", { name: tier.label || String(at + 1), count: tier.items.length }));
    cap.addEventListener("click", () => (selected ? place(selected, tier.id) : openTierSheet(tier.id)));
    const items = document.createElement("div");
    items.className = "tl-items";
    items.setAttribute("aria-label", t("tier {name}", { name: tier.label || String(at + 1) }));
    items.append(...tier.items.map(tileEl));
    items.addEventListener("click", (event) => {
      if (selected && event.target === items) place(selected, tier.id);
    });
    const tools = document.createElement("div");
    tools.className = "tl-tools";
    const up = rowKey("up", t("move tier {name} up", { name: tier.label || String(at + 1) }), at === 0, () => moveTier(at, -1));
    const down = rowKey("down", t("move tier {name} down", { name: tier.label || String(at + 1) }), at === current.tiers.length - 1, () => moveTier(at, 1));
    tools.append(up, down);
    row.append(cap, items, tools);
    return row;
  }));
  pool().replaceChildren(...current.pool.map(tileEl));
  byId("tl-pool-empty").hidden = current.pool.length > 0 || Object.keys(current.items).length === 0;
  byId("tl-blank").hidden = Object.keys(current.items).length > 0;
  byId("tl-tray").classList.toggle("target-ready", Boolean(selected));
  rack().classList.toggle("target-ready", Boolean(selected));
  byId("tl-add-tier").toggleAttribute("disabled", current.tiers.length >= MAX_TIERS);
  paintSelection();
  if (focus) byId("tl-editor").querySelector<HTMLElement>(`.tl-tile[data-id="${focus}"]`)?.focus();
}

function rowKey(icon: string, label: string, disabled: boolean, press: () => void): HTMLButtonElement {
  const key = document.createElement("button");
  key.type = "button";
  key.className = "tl-row-key";
  key.setAttribute("aria-label", label);
  key.title = label;
  key.disabled = disabled;
  key.innerHTML = iconSvg(icon, 18);
  key.addEventListener("click", () => {
    buzz();
    press();
  });
  return key;
}

function moveTier(at: number, by: number): void {
  if (!list) return;
  const to = at + by;
  if (to < 0 || to >= list.tiers.length) return;
  const [tier] = list.tiers.splice(at, 1);
  list.tiers.splice(to, 0, tier);
  render();
  scheduleSave();
  say(t("tier {name} moved to row {row}", { name: tier.label || String(at + 1), row: to + 1 }));
}

// the picked picture's bar: its name, and the ways to put it back or take it away
function paintSelection(): void {
  const bar = byId("tl-selbar");
  bar.hidden = !selected || !list?.items[selected];
  if (bar.hidden) return;
  const input = byId<HTMLInputElement>("tl-item-label");
  if (document.activeElement !== input) input.value = list!.items[selected].label;
  byId("tl-hint").textContent = t("now tap a tier to place it, or drag it there");
}

function select(id: string): void {
  selected = id;
  render(id);
  if (!id) byId("tl-hint").textContent = hintText();
}

function hintText(): string {
  return t("drag a picture to a tier, or tap it and then tap the tier");
}

// puts a picture in a tier (or the tray when tier is empty) at a spot, default the end
function place(id: string, tierId: string, index = -1): void {
  if (!list || !list.items[id]) return;
  for (const tier of list.tiers) tier.items = tier.items.filter((item) => item !== id);
  list.pool = list.pool.filter((item) => item !== id);
  const target = tierId ? list.tiers.find((tier) => tier.id === tierId) : null;
  const into = target ? target.items : list.pool;
  into.splice(index < 0 || index > into.length ? into.length : index, 0, id);
  selected = "";
  render(id);
  byId("tl-hint").textContent = hintText();
  scheduleSave();
  say(`${labelOf(id)}, ${whereIs(id)}, ${t("position {n}", { n: into.indexOf(id) + 1 })}`);
}

// arrow keys on a focused picture: left and right reorder, up and down change tier
function keyMove(event: KeyboardEvent, id: string): void {
  if (!list || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
  event.preventDefault();
  const tierAt = list.tiers.findIndex((tier) => tier.items.includes(id));
  const here = tierAt >= 0 ? list.tiers[tierAt].items : list.pool;
  const at = here.indexOf(id);
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    const to = at + (event.key === "ArrowRight" ? 1 : -1);
    if (to < 0 || to >= here.length) return;
    here.splice(at, 1);
    here.splice(to, 0, id);
    selected = "";
    render(id);
    scheduleSave();
    say(`${labelOf(id)}, ${t("position {n}", { n: to + 1 })}`);
    return;
  }
  // down from the last tier goes to the tray, up from the tray goes to the last tier
  const step = event.key === "ArrowDown" ? 1 : -1;
  const to = (tierAt >= 0 ? tierAt : list.tiers.length) + step;
  if (to < 0 || to > list.tiers.length) return;
  place(id, to === list.tiers.length ? "" : list.tiers[to].id);
}

// --- dragging

function beginDrag(event: PointerEvent, id: string, tile: HTMLElement): void {
  if (event.button !== 0 || drag) return;
  drag = { id, pointer: event.pointerId, x: event.clientX, y: event.clientY, started: false, ghost: null, from: tile };
  try {
    tile.setPointerCapture(event.pointerId);
  } catch {
    // the move events still arrive without it
  }
  tile.addEventListener("pointermove", moveDrag);
  tile.addEventListener("pointerup", endDrag);
  tile.addEventListener("pointercancel", cancelDrag);
}

function dropTarget(x: number, y: number): { tier: string; index: number; zone: HTMLElement } | null {
  const hit = document.elementFromPoint(x, y)?.closest<HTMLElement>(".tl-tier, #tl-tray");
  if (!hit || !list) return null;
  const tier = hit.classList.contains("tl-tier") ? hit.dataset.tier || "" : "";
  const items = hit.querySelector<HTMLElement>(".tl-items, #tl-pool");
  const tiles = items ? Array.from(items.querySelectorAll<HTMLElement>(".tl-tile")).filter((tile) => tile.dataset.id !== drag?.id) : [];
  // the first picture that is further right on its line, or on a lower line, is where this one goes in front of
  let index = tiles.length;
  for (let at = 0; at < tiles.length; at++) {
    const box = tiles[at].getBoundingClientRect();
    const sameLine = y >= box.top && y <= box.bottom;
    if (y < box.top || (sameLine && x < box.left + box.width / 2)) {
      index = at;
      break;
    }
  }
  return { tier, index, zone: hit };
}

function moveDrag(event: PointerEvent): void {
  if (!drag || drag.pointer !== event.pointerId) return;
  if (!drag.started) {
    if (Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 8) return;
    drag.started = true;
    const ghost = drag.from.cloneNode(true) as HTMLElement;
    ghost.classList.add("tl-ghost");
    ghost.removeAttribute("aria-pressed");
    ghost.style.width = `${drag.from.offsetWidth}px`;
    ghost.style.height = `${drag.from.offsetHeight}px`;
    document.body.append(ghost);
    drag.ghost = ghost;
    drag.from.classList.add("lifted");
  }
  if (drag.ghost) {
    drag.ghost.style.transform = `translate(${event.clientX - drag.ghost.offsetWidth / 2}px, ${event.clientY - drag.ghost.offsetHeight / 2}px)`;
  }
  const over = dropTarget(event.clientX, event.clientY);
  document.querySelectorAll(".tl-tier.over, #tl-tray.over").forEach((zone) => zone.classList.remove("over"));
  over?.zone.classList.add("over");
}

function finishDrag(): { id: string; dragged: boolean } {
  const done = drag!;
  drag = null;
  done.from.removeEventListener("pointermove", moveDrag);
  done.from.removeEventListener("pointerup", endDrag);
  done.from.removeEventListener("pointercancel", cancelDrag);
  done.ghost?.remove();
  done.from.classList.remove("lifted");
  document.querySelectorAll(".tl-tier.over, #tl-tray.over").forEach((zone) => zone.classList.remove("over"));
  if (done.started) done.from.dataset.dragged = "1";
  return { id: done.id, dragged: done.started };
}

function endDrag(event: PointerEvent): void {
  if (!drag || drag.pointer !== event.pointerId) return;
  const over = drag.started ? dropTarget(event.clientX, event.clientY) : null;
  const { id, dragged } = finishDrag();
  if (dragged && over) place(id, over.tier, over.index);
}

function cancelDrag(): void {
  if (drag) finishDrag();
}

// --- a tier's own settings: its name, its colour and whether it stays

function openTierSheet(id: string): void {
  const tier = list?.tiers.find((item) => item.id === id);
  if (!tier) return;
  tierEditing = id;
  byId<HTMLInputElement>("tl-tier-label").value = tier.label;
  const swatches = byId("tl-tier-colors");
  swatches.replaceChildren(...TIER_COLORS.map((color) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "tl-swatch";
    key.style.background = color;
    key.setAttribute("aria-label", color);
    key.setAttribute("aria-pressed", String(tier.color.toLowerCase() === color));
    key.addEventListener("click", () => {
      tier.color = color;
      render();
      openTierSheet(id);
      scheduleSave();
    });
    return key;
  }));
  byId<HTMLInputElement>("tl-tier-color").value = tier.color;
  byId("tl-tier-sheet").hidden = false;
  byId<HTMLInputElement>("tl-tier-label").focus();
}

function closeTierSheet(): void {
  byId("tl-tier-sheet").hidden = true;
  const key = tierEditing ? byId("tl-rack").querySelector<HTMLElement>(`.tl-tier[data-tier="${tierEditing}"] .tl-cap`) : null;
  tierEditing = "";
  key?.focus();
}

function deleteTier(): void {
  if (!list || !tierEditing) return;
  const at = list.tiers.findIndex((tier) => tier.id === tierEditing);
  if (at < 0) return;
  const [gone] = list.tiers.splice(at, 1);
  list.pool.push(...gone.items);
  tierEditing = "";
  byId("tl-tier-sheet").hidden = true;
  render();
  scheduleSave();
  say(t("tier removed, its pictures are back in the tray"));
}

// --- pictures in

// shrunk before it is sent, so a list of phone photos stays small and quick
async function shrink(file: File): Promise<TierImageDraft | null> {
  if (!file.type.startsWith("image/")) return null;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, PICTURE_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    let data = canvas.toDataURL("image/webp", 0.86);
    // safari cannot make webp, and jpeg has no see-through, so a flat white sits behind it
    if (!data.startsWith("data:image/webp")) {
      const flat = document.createElement("canvas");
      flat.width = canvas.width;
      flat.height = canvas.height;
      const paint = flat.getContext("2d");
      if (!paint) return null;
      paint.fillStyle = "#ffffff";
      paint.fillRect(0, 0, flat.width, flat.height);
      paint.drawImage(canvas, 0, 0);
      data = flat.toDataURL("image/jpeg", 0.88);
    }
    return { name: file.name || "", data };
  } catch {
    return null;
  }
}

async function addFiles(files: File[]): Promise<void> {
  if (!list || !files.length) return;
  const room = MAX_TIER_ITEMS - Object.keys(list.items).length;
  if (room <= 0) return flashToast(t("a tier list holds {count} pictures", { count: MAX_TIER_ITEMS }));
  const picked = files.filter((file) => file.type.startsWith("image/")).slice(0, room);
  if (!picked.length) return flashToast(t("those are not pictures"));
  const target = list.id;
  busy++;
  byId("tl-busy").hidden = false;
  try {
    let added = 0;
    for (let at = 0; at < picked.length; at += SEND_BATCH) {
      byId("tl-busy").textContent = t("adding pictures {done} of {total}", { done: at, total: picked.length });
      const drafts = (await Promise.all(picked.slice(at, at + SEND_BATCH).map(shrink))).filter((draft): draft is TierImageDraft => Boolean(draft));
      if (!drafts.length) continue;
      // anything edited meanwhile is saved first, so the server's copy is current
      await flushSave();
      const next = await call<TierList>(CHANNELS.tierlistsImages, target, drafts);
      added += drafts.length;
      if (list && list.id === target) {
        list = next;
        render();
      }
    }
    say(t("{count} pictures added", { count: added }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not add those"));
  } finally {
    busy--;
    byId("tl-busy").hidden = busy === 0;
  }
}

// --- the link

function linkFor(token: string): string {
  return `${location.origin}/t/${token}`;
}

function paintLink(): void {
  const on = Boolean(list?.share);
  byId("tl-link").hidden = !on;
  byId("tl-share").classList.toggle("active", on);
  byId("tl-share-text").textContent = on ? t("link") : t("share");
  if (on) byId<HTMLInputElement>("tl-url").value = linkFor(list!.share);
}

async function share(on: boolean): Promise<void> {
  if (!list) return;
  await flushSave();
  try {
    list = await call<TierList>(CHANNELS.tierlistsShare, list.id, on);
    paintLink();
    if (on) byId<HTMLInputElement>("tl-url").select();
    say(on ? t("link is on, anyone who has it can look at this list") : t("link is off"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not change the link"));
  }
}

async function copyLink(): Promise<void> {
  const input = byId<HTMLInputElement>("tl-url");
  try {
    await navigator.clipboard.writeText(input.value);
    flashToast(t("link copied"));
  } catch {
    input.select();
    flashToast(t("press copy on your keyboard to copy the link"));
  }
}

// --- the picture of the list

async function loadImage(url: string): Promise<ImageBitmap | null> {
  try {
    const blob = await (await fetch(mediaSrc(url))).blob();
    return await createImageBitmap(blob);
  } catch {
    return null;
  }
}

async function savePicture(): Promise<void> {
  if (!list) return;
  const current = list;
  const side = 112;
  const cap = 132;
  const width = 1000;
  const perRow = Math.floor((width - cap - 12) / (side + 4));
  const rows = current.tiers.map((tier) => ({ tier, lines: Math.max(1, Math.ceil(tier.items.length / perRow)) }));
  const head = 76;
  const height = head + rows.reduce((sum, row) => sum + row.lines * (side + 4) + 4, 0) + 40;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const images = new Map<string, ImageBitmap | null>();
  await Promise.all(current.tiers.flatMap((tier) => tier.items).map(async (id) => images.set(id, await loadImage(current.items[id].url || ""))));
  ctx.fillStyle = "#090b10";
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = "#e9eef7";
  ctx.font = "700 40px system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText(current.title, 20, 38);
  let y = head;
  for (const { tier, lines } of rows) {
    const rowHeight = lines * (side + 4) + 4;
    ctx.fillStyle = "#0e121b";
    ctx.fillRect(10, y, width - 20, rowHeight);
    ctx.fillStyle = tier.color;
    ctx.fillRect(10, y, cap, rowHeight);
    ctx.fillStyle = capInk(tier.color);
    ctx.font = "700 40px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(tier.label.slice(0, 6), 10 + cap / 2, y + rowHeight / 2, cap - 16);
    ctx.textAlign = "left";
    tier.items.forEach((id, at) => {
      const x = 10 + cap + 4 + (at % perRow) * (side + 4);
      const top = y + 4 + Math.floor(at / perRow) * (side + 4);
      const image = images.get(id);
      if (image) {
        // the picture fills the square, cropped the way it shows on screen
        const crop = Math.min(image.width, image.height);
        ctx.drawImage(image, (image.width - crop) / 2, (image.height - crop) / 2, crop, crop, x, top, side, side);
      } else {
        ctx.fillStyle = "#1b1f2a";
        ctx.fillRect(x, top, side, side);
      }
    });
    y += rowHeight + 0;
  }
  ctx.fillStyle = "#98a3ba";
  ctx.font = "14px system-ui, sans-serif";
  ctx.fillText("made with Chix", 20, height - 18);
  canvas.toBlob((blob) => {
    if (!blob) return flashToast(t("could not make the picture"));
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${current.title.replace(/[^\w-]+/g, "-").replace(/^-+|-+$/g, "") || "tier-list"}.png`;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(link.href), 4000);
  }, "image/png");
}

async function deleteList(): Promise<void> {
  if (!list || !confirm(t("delete {name}? this can't be undone", { name: list.title }))) return;
  const id = list.id;
  list = null;
  window.clearTimeout(saveTimer);
  try {
    await call(CHANNELS.tierlistsRemove, id);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not delete it"));
  }
  byId("tl-editor").hidden = true;
  byId("tl-shelf-block").hidden = false;
  await reloadShelf();
}

export function setupTierlistsView(): void {
  byId("tl-new").addEventListener("click", () => void create());
  byId("tl-back").addEventListener("click", () => void closeList());
  byId("tl-add").addEventListener("click", () => byId<HTMLInputElement>("tl-files").click());
  byId("tl-blank-add").addEventListener("click", () => byId<HTMLInputElement>("tl-files").click());
  byId<HTMLInputElement>("tl-files").addEventListener("change", (event) => {
    const input = event.target as HTMLInputElement;
    void addFiles(Array.from(input.files || []));
    input.value = "";
  });
  byId<HTMLInputElement>("tl-title").addEventListener("input", (event) => {
    if (!list) return;
    list.title = (event.target as HTMLInputElement).value;
    scheduleSave();
  });
  byId("tl-add-tier").addEventListener("click", () => {
    if (!list || list.tiers.length >= MAX_TIERS) return;
    const tier = { id: newTierId(), label: "", color: TIER_COLORS[list.tiers.length % TIER_COLORS.length] as string, items: [] };
    list.tiers.push(tier);
    render();
    scheduleSave();
    openTierSheet(tier.id);
  });
  byId("tl-reset").addEventListener("click", () => {
    if (!list) return;
    for (const tier of list.tiers) {
      list.pool.push(...tier.items);
      tier.items = [];
    }
    selected = "";
    render();
    scheduleSave();
    say(t("every picture is back in the tray"));
  });
  byId("tl-save-picture").addEventListener("click", () => void savePicture());
  byId("tl-delete").addEventListener("click", () => void deleteList());
  byId("tl-share").addEventListener("click", () => {
    if (list?.share) byId<HTMLInputElement>("tl-url").select();
    else void share(true);
  });
  byId("tl-copy").addEventListener("click", () => void copyLink());
  byId("tl-stop").addEventListener("click", () => void share(false));

  byId("tl-tray").addEventListener("click", (event) => {
    if (selected && (event.target === byId("tl-tray") || event.target === pool() || event.target === byId("tl-pool-empty"))) place(selected, "");
  });
  byId<HTMLInputElement>("tl-item-label").addEventListener("input", (event) => {
    if (!list || !selected) return;
    list.items[selected].label = (event.target as HTMLInputElement).value.slice(0, 40);
    const tile = byId("tl-editor").querySelector<HTMLElement>(`.tl-tile[data-id="${selected}"]`);
    if (tile) {
      tile.querySelector("span")?.remove();
      if (list.items[selected].label) {
        const name = document.createElement("span");
        name.textContent = list.items[selected].label;
        tile.append(name);
      }
    }
    scheduleSave();
  });
  byId("tl-item-back").addEventListener("click", () => selected && place(selected, ""));
  byId("tl-item-remove").addEventListener("click", () => {
    if (!list || !selected) return;
    const id = selected;
    delete list.items[id];
    for (const tier of list.tiers) tier.items = tier.items.filter((item) => item !== id);
    list.pool = list.pool.filter((item) => item !== id);
    selected = "";
    render();
    scheduleSave();
    say(t("picture removed"));
  });
  byId("tl-item-done").addEventListener("click", () => select(""));

  byId<HTMLInputElement>("tl-tier-label").addEventListener("input", (event) => {
    const tier = list?.tiers.find((item) => item.id === tierEditing);
    if (!tier) return;
    tier.label = (event.target as HTMLInputElement).value.slice(0, 40);
    const cap = byId("tl-rack").querySelector<HTMLElement>(`.tl-tier[data-tier="${tierEditing}"] .tl-cap`);
    if (cap) cap.textContent = tier.label || "·";
    scheduleSave();
  });
  byId<HTMLInputElement>("tl-tier-color").addEventListener("input", (event) => {
    const tier = list?.tiers.find((item) => item.id === tierEditing);
    if (!tier) return;
    tier.color = (event.target as HTMLInputElement).value;
    render();
    scheduleSave();
  });
  byId("tl-tier-close").addEventListener("click", closeTierSheet);
  byId("tl-tier-delete").addEventListener("click", deleteTier);
  byId("tl-tier-sheet").addEventListener("click", (event) => {
    if (event.target === byId("tl-tier-sheet")) closeTierSheet();
  });
  byId("tl-tier-sheet").addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeTierSheet();
    if (event.key === "Enter" && (event.target as HTMLElement).id === "tl-tier-label") closeTierSheet();
  });

  // pictures dropped on the page or pasted from the clipboard
  const editor = byId("tl-editor");
  editor.addEventListener("dragover", (event) => {
    if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
  });
  editor.addEventListener("drop", (event) => {
    if (!event.dataTransfer?.files.length) return;
    event.preventDefault();
    void addFiles(Array.from(event.dataTransfer.files));
  });
  window.addEventListener("paste", (event) => {
    if (!list || editor.hidden || (event.target as HTMLElement | null)?.closest("input, textarea")) return;
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length) {
      event.preventDefault();
      void addFiles(files);
    }
  });
  window.addEventListener("keydown", (event) => {
    if (!list || editor.hidden || event.key !== "Escape" || !byId("tl-tier-sheet").hidden) return;
    if (selected) select("");
  });

  listen(CHANNELS.tierlistsUpdated, () => {
    if (editor.hidden) void reloadShelf();
  });
  onViewShown("tierlists", () => {
    byId("tl-hint").textContent = hintText();
    if (editor.hidden) void reloadShelf();
  });
  onViewHidden("tierlists", () => void flushSave());
}
